import React, { useState, useEffect } from 'react'
import { 
  Users, 
  Building2, 
  Plus, 
  Settings, 
  CheckCircle, 
  XCircle, 
  Eye, 
  Edit3, 
  FileText,
  Calculator,
  ShoppingBag,
  Shield,
  UserPlus,
  Search,
  X,
  ChevronDown,
  Mail,
  Phone,
  Briefcase,
  Lock,
  Unlock,
  Crown,
  Scale,
  User,
  Sparkles,
  Key,
  KeyRound,
  RefreshCw,
  MoreVertical,
  Smartphone,
  AlertCircle
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useExchange } from '../contexts/ExchangeContext'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../components/ui/DropdownMenu'
import { 
  PermissionLevel, 
  UserType, 
  ExchangeUserPermission,
  FeaturePermissions,
  AccountUser
} from '../types/exchange'
import { exchangeApi } from '../services/api/exchange'

interface UserWithPermissions {
  id: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  accountRole?: 'owner' | 'admin' | 'member' | 'viewer'
  exchangePermissions?: ExchangeUserPermission[]
  allowedExchanges?: string[] // Exchange IDs user has access to
}

const USER_TYPE_LABELS: Record<UserType, string> = {
  owner: 'Owner',
  accounting: 'Accounting',
  real_estate_agent: 'Real Estate Agent',
  attorney: 'Attorney',
  member: 'Member',
  viewer: 'Viewer',
  custom: 'Custom'
}

const USER_TYPE_DESCRIPTIONS: Record<UserType, string> = {
  owner: 'Full access to all features',
  accounting: 'Access to IRS forms and financial data',
  real_estate_agent: 'Access to upload documents and property info',
  attorney: 'Access to legal documents and compliance',
  member: 'Standard user access',
  viewer: 'Read-only access',
  custom: 'Custom permissions and feature access'
}

const USER_TYPE_ICONS: Record<UserType, any> = {
  owner: Crown,
  accounting: Calculator,
  real_estate_agent: Briefcase,
  attorney: Scale,
  member: User,
  viewer: Eye,
  custom: Sparkles
}

const PERMISSION_LABELS: Record<PermissionLevel, string> = {
  read: 'Read',
  write: 'Read & Write',
  read_only: 'Read Only'
}

const PERMISSION_ICONS: Record<PermissionLevel, any> = {
  read: Eye,
  write: Edit3,
  read_only: Lock
}

const FEATURE_LABELS = {
  irs_forms: 'IRS Forms',
  documents: 'Documents',
  wizard: 'Exchange Wizard',
  calculator: 'Calculator',
  marketplace: 'Marketplace'
}

const FEATURE_ICONS = {
  irs_forms: FileText,
  documents: FileText,
  wizard: Building2,
  calculator: Calculator,
  marketplace: ShoppingBag
}

// Define which features each user type can access
const USER_TYPE_FEATURES: Record<UserType, (keyof typeof FEATURE_LABELS)[] | 'custom'> = {
  owner: ['irs_forms', 'documents', 'wizard', 'calculator', 'marketplace'], // All features
  accounting: ['irs_forms', 'documents', 'calculator'], // Financial/forms related
  real_estate_agent: ['documents', 'wizard', 'marketplace'], // Property/document related
  attorney: ['documents', 'irs_forms'], // Legal/compliance related
  member: ['documents', 'wizard', 'calculator', 'marketplace'], // Standard access
  viewer: ['irs_forms', 'documents', 'wizard', 'calculator', 'marketplace'], // All features (read-only)
  custom: 'custom' // Custom - user selects features
}

export const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const { currentExchange, exchanges } = useExchange()
  const [users, setUsers] = useState<UserWithPermissions[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showAddUser, setShowAddUser] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedExchange, setSelectedExchange] = useState<string | 'account' | null>(null)
  const [editingPermission, setEditingPermission] = useState<{
    userId: string
    exchangeId: string | 'account'
  } | null>(null)
  const [editingAccountUser, setEditingAccountUser] = useState<AccountUser | undefined>(undefined)
  const [managingUser, setManagingUser] = useState<{
    user: UserWithPermissions
    initialTab?: 'permissions' | 'password' | 'mfa' | 'settings'
  } | null>(null)

  // Load users based on selected exchange or account
  useEffect(() => {
    const loadUsers = async () => {
      if (!selectedExchange) {
        setSelectedExchange('account')
        return
      }

      setIsLoading(true)
      try {
        if (selectedExchange === 'account') {
          // Load account users
          const accounts = await exchangeApi.getAccounts()
          if (accounts.length > 0) {
            const accountUsers = await exchangeApi.getAccountUsers(accounts[0].id)
            // Get user details for each account user
            const enrichedUsers = await Promise.all(
              accountUsers.map(async (au) => {
                const userSearch = await exchangeApi.searchUsers('', accounts[0].id)
                const userData = userSearch.find(u => u.id === au.userId)
                return {
                  id: au.userId,
                  email: userData?.email || '',
                  firstName: userData?.firstName || '',
                  lastName: userData?.lastName || '',
                  phone: userData?.phone,
                  accountRole: au.role,
                  exchangePermissions: [],
                  allowedExchanges: au.allowedExchanges
                }
              })
            )
            
            // Filter out users with empty names
            const validUsers = enrichedUsers.filter(u => u.firstName && u.lastName)
            
            // Add mock users for demo purposes
            const mockUsers: UserWithPermissions[] = [
              {
                id: 'mock_user_1',
                email: 'john.doe@open1031.ai',
                firstName: 'John',
                lastName: 'Doe',
                phone: '555-123-4567',
                accountRole: 'admin',
                exchangePermissions: [],
                allowedExchanges: []
              },
              {
                id: 'mock_user_2',
                email: 'sarah.johnson@open1031.ai',
                firstName: 'Sarah',
                lastName: 'Johnson',
                phone: '555-987-6543',
                accountRole: 'member',
                exchangePermissions: [],
                allowedExchanges: []
              }
            ]
            
            setUsers([...validUsers, ...mockUsers])
          }
        } else {
          // Load exchange users
          const exchangeUsers = await exchangeApi.getExchangeUsers(selectedExchange)
          const enrichedUsers = exchangeUsers.map((eu: any) => ({
            id: eu.user?.id || eu.userId,
            email: eu.user?.email || '',
            firstName: eu.user?.firstName || '',
            lastName: eu.user?.lastName || '',
            accountRole: undefined,
            exchangePermissions: [eu]
          }))
          setUsers(enrichedUsers)
        }
      } catch (error) {
        console.error('Failed to load users:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadUsers()
  }, [selectedExchange, currentExchange, currentUser])

  // Add current logged-in user to the list with owner role
  const allUsers = React.useMemo(() => {
    if (!currentUser) return users
    
    const currentUserInList = users.find(u => u.id === String(currentUser.id))
    if (currentUserInList) {
      // Update existing user to ensure owner role
      return users.map(u => 
        u.id === String(currentUser.id) 
          ? { ...u, accountRole: 'owner' as const }
          : u
      )
    }
    
    // Add current user if not in list
    return [
      {
        id: String(currentUser.id),
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        phone: currentUser.phone,
        accountRole: 'owner' as const,
        exchangePermissions: []
      },
      ...users
    ]
  }, [users, currentUser])

  const filteredUsers = React.useMemo(() => {
    let usersToFilter = allUsers
    
    // For newuser, only show themselves
    if (currentUser?.email === 'newuser@open1031.ai') {
      usersToFilter = allUsers.filter(u => u.id === String(currentUser.id))
    }
    
    const filtered = usersToFilter.filter(u => 
      !searchQuery || 
      u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
    )
    
    // Sort so current user appears first
    return filtered.sort((a, b) => {
      const aIsCurrent = a.id === String(currentUser?.id)
      const bIsCurrent = b.id === String(currentUser?.id)
      if (aIsCurrent && !bIsCurrent) return -1
      if (!aIsCurrent && bIsCurrent) return 1
      return 0
    })
  }, [allUsers, searchQuery, currentUser])

  const handleAddUser = async (
    email: string, 
    exchangeId: string | 'account', 
    userType: UserType, 
    permissionLevel: PermissionLevel, 
    featurePermissions?: FeaturePermissions,
    customTypeName?: string,
    allowedExchanges?: string[]
  ) => {
    try {
      if (exchangeId === 'account') {
        // Add to account - need to search for user first or create invite
        const accounts = await exchangeApi.getAccounts()
        if (accounts.length === 0) {
          throw new Error('No account found')
        }
        
        // Search for existing user
        const searchResults = await exchangeApi.searchUsers(email)
        const existingUser = searchResults.find(u => u.email === email)
        
        if (existingUser) {
          await exchangeApi.addAccountUser(accounts[0].id, {
            userId: existingUser.id,
            role: permissionLevel === 'write' ? 'admin' : permissionLevel === 'read_only' ? 'viewer' : 'member',
            allowedExchanges: allowedExchanges && allowedExchanges.length > 0 ? allowedExchanges : undefined
          })
        } else {
          // Would need to create invite system for account-level invites
          throw new Error('User not found. Please invite them via exchange first.')
        }
      } else {
        // Add to exchange
        await exchangeApi.addExchangeUser(exchangeId, {
          email,
          permissionLevel,
          userType,
          featurePermissions,
          customTypeName
        })
      }
      
      // Refresh users
      setIsLoading(true)
      const accounts = await exchangeApi.getAccounts()
      if (accounts.length > 0 && exchangeId === 'account') {
        const accountUsers = await exchangeApi.getAccountUsers(accounts[0].id)
        const enrichedUsers = await Promise.all(
          accountUsers.map(async (au) => {
            const userSearch = await exchangeApi.searchUsers('', accounts[0].id)
            const userData = userSearch.find(u => u.id === au.userId)
            return {
              id: au.userId,
              email: userData?.email || '',
              firstName: userData?.firstName || '',
              lastName: userData?.lastName || '',
              phone: userData?.phone,
              accountRole: au.role,
              exchangePermissions: []
            }
          })
        )
        setUsers(enrichedUsers)
      } else if (exchangeId !== 'account') {
        const exchangeUsers = await exchangeApi.getExchangeUsers(exchangeId)
        const enrichedUsers = exchangeUsers.map((eu: any) => ({
          id: eu.user?.id || eu.userId,
          email: eu.user?.email || '',
          firstName: eu.user?.firstName || '',
          lastName: eu.user?.lastName || '',
          accountRole: undefined,
          exchangePermissions: [eu]
        }))
        setUsers(enrichedUsers)
      }
      setIsLoading(false)
      setShowAddUser(false)
    } catch (error: any) {
      console.error('Failed to add user:', error)
      alert(error.message || 'Failed to add user')
    }
  }

  const handleUpdatePermission = async (
    userId: string,
    exchangeId: string | 'account',
    updates: Partial<ExchangeUserPermission> & { allowedExchanges?: string[] }
  ) => {
    try {
      if (exchangeId === 'account') {
        // Update account user with allowedExchanges
        const accounts = await exchangeApi.getAccounts()
        if (accounts.length > 0) {
          // Would need account user update endpoint - for now, we'll need to add it
          // For now, let's update the local state
          const accountUsers = await exchangeApi.getAccountUsers(accounts[0].id)
          const accountUser = accountUsers.find(au => au.userId === userId)
          if (accountUser) {
            // Update via API when endpoint is available
            // For now, show error
            throw new Error('Account user updates with exchange selection not yet implemented in API')
          }
        }
      } else {
        // Update exchange permission
        await exchangeApi.updateExchangeUserPermission(exchangeId, userId, updates)
        
        // Refresh exchange users
        const exchangeUsers = await exchangeApi.getExchangeUsers(exchangeId)
        const enrichedUsers = exchangeUsers.map((eu: any) => ({
          id: eu.user?.id || eu.userId,
          email: eu.user?.email || '',
          firstName: eu.user?.firstName || '',
          lastName: eu.user?.lastName || '',
          accountRole: undefined,
          exchangePermissions: [eu]
        }))
        setUsers(enrichedUsers)
      }
      setEditingPermission(null)
      setEditingAccountUser(undefined)
    } catch (error: any) {
      console.error('Failed to update permission:', error)
      alert(error.message || 'Failed to update permission')
    }
  }

  const handlePasswordReset = async (userId: string) => {
    if (!confirm('Are you sure you want to reset the password for this user? They will receive an email with instructions to set a new password.')) {
      return
    }
    try {
      // TODO: Implement password reset API call
      alert('Password reset email sent successfully')
    } catch (error: any) {
      console.error('Failed to reset password:', error)
      alert(error.message || 'Failed to reset password')
    }
  }

  const handleMFAReset = async (userId: string) => {
    if (!confirm('Are you sure you want to reset MFA for this user? They will need to set up MFA again on their next login.')) {
      return
    }
    try {
      // TODO: Implement MFA reset API call
      alert('MFA reset successfully. User will need to set up MFA again on next login.')
    } catch (error: any) {
      console.error('Failed to reset MFA:', error)
      alert(error.message || 'Failed to reset MFA')
    }
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white dark:bg-slate-800/60 rounded-lg shadow-sm dark:shadow-slate-900/50 border border-secondary-200 dark:border-slate-700/50 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-slate-100 flex items-center gap-3">
              <Users className="h-7 w-7 text-primary-600 dark:text-primary-400" />
              User Management
            </h1>
            <p className="text-secondary-600 dark:text-slate-400 mt-1">
              Manage account users and exchange-specific permissions
            </p>
          </div>
          <Button onClick={() => setShowAddUser(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Tabs: Account vs Exchange */}
      <div className="flex space-x-2 border-b border-slate-200">
        <button
          onClick={() => setSelectedExchange('account')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            selectedExchange === 'account'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Users className="h-4 w-4 inline mr-2" />
          Account Users
        </button>
        {currentExchange && (
          <button
            onClick={() => setSelectedExchange(currentExchange.id)}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              selectedExchange === currentExchange.id
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Building2 className="h-4 w-4 inline mr-2" />
            {currentExchange.name} Permissions
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search users by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* User List */}
      <div className="space-y-4">
        {isLoading ? (
          <Card className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-4 text-slate-600">Loading users...</p>
          </Card>
        ) : filteredUsers.length === 0 ? (
          <Card className="p-8 text-center">
            <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No users found</p>
          </Card>
        ) : (
          filteredUsers.map((user) => (
            <UserPermissionCard
              key={user.id}
              user={user}
              isCurrentUser={user.id === String(currentUser?.id)}
              selectedExchange={selectedExchange}
              currentExchangeId={currentExchange?.id}
              exchanges={exchanges}
              onEdit={async () => {
                const permission = { userId: user.id, exchangeId: selectedExchange || 'account' }
                setEditingPermission(permission)
                if (selectedExchange === 'account') {
                  const accounts = await exchangeApi.getAccounts()
                  if (accounts.length > 0) {
                    const accountUsers = await exchangeApi.getAccountUsers(accounts[0].id)
                    const accountUser = accountUsers.find(au => au.userId === user.id)
                    setEditingAccountUser(accountUser)
                  }
                } else {
                  setEditingAccountUser(undefined)
                }
              }}
              onUpdatePermission={handleUpdatePermission}
              onPasswordReset={() => handlePasswordReset(user.id)}
              onMFAReset={() => handleMFAReset(user.id)}
              onManage={(tab) => {
                setManagingUser({ user, initialTab: tab })
                if (selectedExchange === 'account') {
                  const accounts = exchangeApi.getAccounts()
                  accounts.then(async (accs) => {
                    if (accs.length > 0) {
                      const accountUsers = await exchangeApi.getAccountUsers(accs[0].id)
                      const accountUser = accountUsers.find(au => au.userId === user.id)
                      setEditingAccountUser(accountUser)
                    }
                  })
                } else {
                  setEditingPermission({ userId: user.id, exchangeId: selectedExchange || 'account' })
                }
              }}
            />
          ))
        )}
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <AddUserModal
          exchanges={exchanges}
          currentExchange={currentExchange}
          onClose={() => setShowAddUser(false)}
          onAdd={handleAddUser}
        />
      )}

      {/* User Management Modal */}
      {managingUser && (
        <UserManagementModal
          user={managingUser.user}
          initialTab={managingUser.initialTab || 'permissions'}
          selectedExchange={selectedExchange}
          exchanges={exchanges}
          currentExchange={currentExchange}
          currentPermission={selectedExchange !== 'account' 
            ? managingUser.user.exchangePermissions?.find(p => p.exchangeId === selectedExchange)
            : undefined}
          accountUser={editingAccountUser}
          onClose={() => {
            setManagingUser(null)
            setEditingPermission(null)
            setEditingAccountUser(undefined)
          }}
          onSave={handleUpdatePermission}
          onPasswordReset={handlePasswordReset}
          onMFAReset={handleMFAReset}
        />
      )}

      {/* Edit Permission Modal */}
      {editingPermission && !managingUser && (
        <EditPermissionModal
          user={users.find(u => u.id === editingPermission.userId)!}
          exchangeId={editingPermission.exchangeId}
          exchange={editingPermission.exchangeId !== 'account' ? exchanges.find(e => e.id === editingPermission.exchangeId) : null}
          exchanges={exchanges}
          currentPermission={editingPermission.exchangeId !== 'account' 
            ? users.find(u => u.id === editingPermission.userId)?.exchangePermissions?.find(p => p.exchangeId === editingPermission.exchangeId)
            : undefined}
          accountUser={editingAccountUser}
          onClose={() => {
            setEditingPermission(null)
            setEditingAccountUser(undefined)
          }}
          onSave={handleUpdatePermission}
        />
      )}
    </div>
  )
}

const UserPermissionCard: React.FC<{
  user: UserWithPermissions
  isCurrentUser?: boolean
  selectedExchange: string | 'account' | null
  currentExchangeId?: string
  exchanges: any[]
  onEdit: () => void
  onUpdatePermission: (userId: string, exchangeId: string | 'account', updates: Partial<ExchangeUserPermission>) => void
  onPasswordReset: (userId: string) => void
  onMFAReset: (userId: string) => void
  onManage?: (tab: 'permissions' | 'password' | 'mfa' | 'settings') => void
}> = ({ user, isCurrentUser = false, selectedExchange, currentExchangeId, exchanges, onEdit, onUpdatePermission, onPasswordReset, onMFAReset, onManage }) => {
  const permission = selectedExchange === 'account'
    ? undefined
    : selectedExchange && user.exchangePermissions?.find(p => p.exchangeId === selectedExchange)

  const accountRole = user.accountRole || 'member'
  
  // Get exchange names for allowed exchanges
  const userExchanges = user.allowedExchanges && user.allowedExchanges.length > 0
    ? exchanges.filter(ex => user.allowedExchanges?.includes(ex.id))
    : exchanges // If no allowedExchanges, they have access to all

  return (
    <Card className={`p-6 hover:shadow-lg transition-shadow ${isCurrentUser ? 'ring-2 ring-primary-500/20 bg-gradient-to-br from-primary-50/30 to-white' : ''}`}>
      <div className="flex items-center justify-between gap-4">
        {/* User Info Section */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className={`w-12 h-12 rounded-full bg-black border-2 border-slate-300 flex items-center justify-center text-white font-bold text-lg relative flex-shrink-0 ${isCurrentUser ? 'ring-2 ring-primary-500/50' : ''}`}>
            {(user.firstName && user.lastName) ? `${user.firstName[0]}${user.lastName[0]}` : (user.email ? user.email[0].toUpperCase() : 'U')}
            {isCurrentUser && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                <CheckCircle className="h-2.5 w-2.5 text-white" />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <User className="h-4 w-4 text-primary-600" />
                {user.firstName} {user.lastName}
              </h3>
              {isCurrentUser && (
                <Badge label="You" variant="success" icon={<User className="h-3 w-3" />} />
              )}
              {selectedExchange === 'account' ? (
                <>
                  <Badge 
                    label={`Account ${accountRole.charAt(0).toUpperCase() + accountRole.slice(1)}`} 
                    variant={accountRole === 'owner' ? 'success' : accountRole === 'admin' ? 'info' : 'default'} 
                    icon={accountRole === 'owner' ? <Crown className="h-3 w-3" /> : <Shield className="h-3 w-3" />}
                  />
                </>
              ) : permission ? (
                <>
                  <Badge 
                    label={permission.customTypeName || USER_TYPE_LABELS[permission.userType]} 
                    variant="info" 
                    icon={USER_TYPE_ICONS[permission.userType] ? React.createElement(USER_TYPE_ICONS[permission.userType], { className: "h-3 w-3" }) : undefined}
                  />
                  <Badge 
                    label={PERMISSION_LABELS[permission.permissionLevel]} 
                    variant="default" 
                    icon={PERMISSION_ICONS[permission.permissionLevel] ? React.createElement(PERMISSION_ICONS[permission.permissionLevel], { className: "h-3 w-3" }) : undefined}
                  />
                </>
              ) : (
                <Badge label="No permissions set" variant="warning" icon={<AlertCircle className="h-3 w-3" />} />
              )}
              {/* Email and Phone on same line */}
              <span className="flex items-center text-sm text-slate-600 dark:text-slate-300 ml-2">
                <Mail className="h-3 w-3 mr-1 text-slate-500 dark:text-slate-400" />
                {user.email}
              </span>
              {user.phone && (
                <span className="flex items-center text-sm text-slate-600 dark:text-slate-300">
                  <Phone className="h-3 w-3 mr-1 text-slate-500 dark:text-slate-400" />
                  {user.phone}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action Icons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onManage ? onManage('permissions') : onEdit()}
            className="h-9 w-9 p-0"
            title="Manage Permissions"
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onManage ? onManage('password') : onPasswordReset(user.id)}
            className="h-9 w-9 p-0"
            title="Reset Password"
          >
            <Key className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onManage ? onManage('mfa') : onMFAReset(user.id)}
            className="h-9 w-9 p-0"
            title="Reset MFA"
          >
            <Smartphone className="h-4 w-4" />
          </Button>
          {isCurrentUser && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onManage ? onManage('settings') : undefined}
              className="h-9 w-9 p-0"
              title="Refresh Session"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Exchange Access Tags - Show for account users */}
      {selectedExchange === 'account' && userExchanges.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-600 dark:text-white mr-2">Exchange Access:</span>
            {user.allowedExchanges && user.allowedExchanges.length > 0 ? (
              userExchanges.map((ex) => (
                <Badge
                  key={ex.id}
                  label={ex.name}
                  variant="info"
                  icon={<Building2 className="h-3 w-3" />}
                />
              ))
            ) : (
              <Badge
                label="All Exchanges"
                variant="success"
                icon={<Building2 className="h-3 w-3" />}
              />
            )}
          </div>
        </div>
      )}

      {/* Feature Permissions - Show below if exchange permission exists */}
      {permission && permission.featurePermissions && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {Object.entries(permission.featurePermissions).map(([feature, enabled]) => {
              const Icon = FEATURE_ICONS[feature as keyof typeof FEATURE_ICONS]
              return (
                <div
                  key={feature}
                  className={`flex items-center gap-2 p-2 rounded-lg border ${
                    enabled 
                      ? 'bg-green-50 text-green-700 border-green-200' 
                      : 'bg-slate-50 text-slate-400 border-slate-200'
                  }`}
                >
                  {Icon && <Icon className="h-3 w-3" />}
                  <span className="text-xs font-medium flex-1">
                    {FEATURE_LABELS[feature as keyof typeof FEATURE_LABELS]}
                  </span>
                  {enabled ? (
                    <CheckCircle className="h-3 w-3 text-green-600" />
                  ) : (
                    <XCircle className="h-3 w-3 text-slate-400" />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </Card>
  )
}

const Badge: React.FC<{ label: string; variant: 'success' | 'default' | 'info' | 'warning'; icon?: React.ReactNode }> = ({ label, variant, icon }) => {
  const variants = {
    success: 'bg-green-100 text-green-700 border-green-200',
    default: 'bg-slate-100 text-slate-700 border-slate-200',
    info: 'bg-blue-100 text-blue-700 border-blue-200',
    warning: 'bg-yellow-100 text-yellow-700 border-yellow-200'
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${variants[variant]}`}>
      {icon}
      {label}
    </span>
  )
}

const AddUserModal: React.FC<{
  exchanges: any[]
  currentExchange: any
  onClose: () => void
  onAdd: (email: string, exchangeId: string | 'account', userType: UserType, permissionLevel: PermissionLevel, featurePermissions?: FeaturePermissions, customTypeName?: string, allowedExchanges?: string[]) => void
}> = ({ exchanges, currentExchange, onClose, onAdd }) => {
  const [email, setEmail] = useState('')
  const [scope, setScope] = useState<'account' | 'exchange'>('account')
  const [selectedExchange, setSelectedExchange] = useState(currentExchange?.id || '')
  const [userType, setUserType] = useState<UserType>('member')
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>('read')
  const [featurePermissions, setFeaturePermissions] = useState<FeaturePermissions>({})
  const [customTypeName, setCustomTypeName] = useState('')
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>([])

  const toggleFeature = (feature: keyof FeaturePermissions) => {
    setFeaturePermissions(prev => ({
      ...prev,
      [feature]: !prev[feature]
    }))
  }

  const toggleExchange = (exchangeId: string) => {
    setSelectedExchanges(prev => 
      prev.includes(exchangeId)
        ? prev.filter(id => id !== exchangeId)
        : [...prev, exchangeId]
    )
  }

  const selectAllExchanges = () => {
    setSelectedExchanges(exchanges.map(e => e.id))
  }

  const deselectAllExchanges = () => {
    setSelectedExchanges([])
  }

  const handleAdd = () => {
    onAdd(
      email,
      scope === 'account' ? 'account' : selectedExchange,
      userType,
      permissionLevel,
      scope === 'exchange' ? featurePermissions : undefined,
      userType === 'custom' ? customTypeName : undefined,
      scope === 'account' ? selectedExchanges : undefined
    )
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-3xl rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.12),0_4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_4px_12px_rgba(0,0,0,0.2)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200/50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <UserPlus className="h-6 w-6 text-primary-600" />
              Add User
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100/80 rounded-xl transition-colors">
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Email Address
            </label>
            <Input
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white/60 backdrop-blur-sm border-slate-200/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">
              Scope
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setScope('account')}
                className={`p-4 rounded-2xl border-2 transition-all ${
                  scope === 'account'
                    ? 'border-primary-500 bg-primary-50/80 backdrop-blur-sm shadow-lg shadow-primary-500/10'
                    : 'border-slate-200 hover:border-slate-300 bg-white/60 backdrop-blur-sm'
                }`}
              >
                <Users className={`h-6 w-6 mx-auto mb-2 ${scope === 'account' ? 'text-primary-600' : 'text-slate-400'}`} />
                <div className={`font-medium ${scope === 'account' ? 'text-primary-700' : 'text-slate-700'}`}>Account Level</div>
                <div className="text-xs text-slate-600 mt-1">Select exchanges</div>
              </button>
              <button
                onClick={() => setScope('exchange')}
                className={`p-4 rounded-2xl border-2 transition-all ${
                  scope === 'exchange'
                    ? 'border-primary-500 bg-primary-50/80 backdrop-blur-sm shadow-lg shadow-primary-500/10'
                    : 'border-slate-200 hover:border-slate-300 bg-white/60 backdrop-blur-sm'
                }`}
              >
                <Building2 className={`h-6 w-6 mx-auto mb-2 ${scope === 'exchange' ? 'text-primary-600' : 'text-slate-400'}`} />
                <div className={`font-medium ${scope === 'exchange' ? 'text-primary-700' : 'text-slate-700'}`}>Exchange Level</div>
                <div className="text-xs text-slate-600 mt-1">Specific exchange</div>
              </button>
            </div>
          </div>

          {scope === 'exchange' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Exchange
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between bg-white/60 backdrop-blur-sm border-slate-200/50">
                    {exchanges.find(e => e.id === selectedExchange)?.name || 'Select exchange'}
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-full">
                  {exchanges.map((exchange) => (
                    <DropdownMenuItem
                      key={exchange.id}
                      onClick={() => setSelectedExchange(exchange.id)}
                    >
                      {exchange.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {scope === 'account' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Exchanges
              </label>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-slate-600">
                  {selectedExchanges.length === 0 
                    ? 'No exchanges selected (user will have access to all exchanges)'
                    : `${selectedExchanges.length} of ${exchanges.length} exchanges selected`
                  }
                </span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={selectAllExchanges}
                    className="text-xs"
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={deselectAllExchanges}
                    className="text-xs"
                  >
                    Clear All
                  </Button>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto border border-slate-200/50 rounded-xl bg-white/60 backdrop-blur-sm p-3 space-y-2">
                {exchanges.length === 0 ? (
                  <div className="text-sm text-slate-500 text-center py-4">No exchanges available</div>
                ) : (
                  exchanges.map((exchange) => {
                    const isSelected = selectedExchanges.includes(exchange.id)
                    return (
                      <button
                        key={exchange.id}
                        onClick={() => toggleExchange(exchange.id)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50/80 backdrop-blur-sm'
                            : 'border-slate-200 hover:border-slate-300 bg-white/60 backdrop-blur-sm'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Building2 className={`h-4 w-4 ${isSelected ? 'text-primary-600' : 'text-slate-400'}`} />
                          <div className="text-left">
                            <div className={`text-sm font-medium ${isSelected ? 'text-primary-700' : 'text-slate-700'}`}>
                              {exchange.name}
                            </div>
                            {exchange.address && (
                              <div className="text-xs text-slate-500 mt-0.5">{exchange.address}</div>
                            )}
                          </div>
                        </div>
                        {isSelected ? (
                          <CheckCircle className="h-5 w-5 text-primary-600" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-slate-300" />
                        )}
                      </button>
                    )
                  })
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">
              User Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {(['owner', 'accounting', 'real_estate_agent', 'attorney', 'member', 'viewer', 'custom'] as UserType[]).map((type) => {
                const Icon = USER_TYPE_ICONS[type]
                const isSelected = userType === type
                return (
                  <button
                    key={type}
                    onClick={() => setUserType(type)}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50/80 backdrop-blur-sm shadow-lg shadow-primary-500/10'
                        : 'border-slate-200 hover:border-slate-300 bg-white/60 backdrop-blur-sm'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Icon className={`h-5 w-5 ${isSelected ? 'text-primary-600' : 'text-slate-400'}`} />
                      <div className={`font-medium text-sm ${isSelected ? 'text-primary-700' : 'text-slate-700'}`}>{USER_TYPE_LABELS[type]}</div>
                    </div>
                    <div className="text-xs text-slate-600">{USER_TYPE_DESCRIPTIONS[type]}</div>
                  </button>
                )
              })}
            </div>
            {/* Show access level description */}
            <div className="mt-3 p-3 rounded-xl bg-slate-50/80 backdrop-blur-sm border border-slate-200/50">
              <div className="flex items-start gap-2">
                <Shield className="h-4 w-4 text-primary-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-1">Access Level:</div>
                  <div className="text-xs text-slate-600">{USER_TYPE_DESCRIPTIONS[userType]}</div>
                </div>
              </div>
            </div>
            {/* Custom type name input when custom is selected */}
            {userType === 'custom' && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Custom Type Name
                </label>
                <Input
                  placeholder="e.g., Project Manager, Consultant, etc."
                  value={customTypeName || ''}
                  onChange={(e) => setCustomTypeName(e.target.value)}
                  className="bg-white/60 backdrop-blur-sm border-slate-200/50"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">
              Permission Level
            </label>
            <div className="grid grid-cols-3 gap-3">
              {(['read', 'write', 'read_only'] as PermissionLevel[]).map((level) => {
                const Icon = PERMISSION_ICONS[level]
                const isSelected = permissionLevel === level
                return (
                  <button
                    key={level}
                    onClick={() => setPermissionLevel(level)}
                    className={`p-4 rounded-2xl border-2 transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50/80 backdrop-blur-sm shadow-lg shadow-primary-500/10'
                        : 'border-slate-200 hover:border-slate-300 bg-white/60 backdrop-blur-sm'
                    }`}
                  >
                    <Icon className={`h-5 w-5 mx-auto mb-2 ${isSelected ? 'text-primary-600' : 'text-slate-400'}`} />
                    <div className={`font-medium text-sm text-center ${isSelected ? 'text-primary-700' : 'text-slate-700'}`}>{PERMISSION_LABELS[level]}</div>
                  </button>
                )
              })}
            </div>
          </div>

          {scope === 'exchange' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Feature Permissions
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(FEATURE_LABELS)
                  .filter(([feature]) => {
                    const typeFeatures = USER_TYPE_FEATURES[userType]
                    return typeFeatures === 'custom' || typeFeatures.includes(feature as keyof typeof FEATURE_LABELS)
                  })
                  .map(([feature, label]) => {
                    const Icon = FEATURE_ICONS[feature as keyof typeof FEATURE_ICONS]
                    const enabled = featurePermissions[feature as keyof FeaturePermissions] || false
                    return (
                      <button
                        key={feature}
                        onClick={() => toggleFeature(feature as keyof FeaturePermissions)}
                        className={`p-4 rounded-2xl border-2 transition-all text-left ${
                          enabled
                            ? 'border-primary-500 bg-primary-50/80 backdrop-blur-sm shadow-lg shadow-primary-500/10'
                            : 'border-slate-200 hover:border-slate-300 bg-white/60 backdrop-blur-sm'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          {Icon && <Icon className={`h-5 w-5 ${enabled ? 'text-primary-600' : 'text-slate-400'}`} />}
                          {enabled ? (
                            <CheckCircle className="h-5 w-5 text-primary-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-slate-400" />
                          )}
                        </div>
                        <div className={`font-medium text-sm ${enabled ? 'text-primary-700' : 'text-slate-700'}`}>{label}</div>
                      </button>
                    )
                  })}
              </div>
            </div>
          )}

        </div>

        <div className="p-6 border-t border-slate-200/50 flex-shrink-0">
          <div className="flex space-x-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleAdd}
              className="flex-1"
              disabled={!email || (scope === 'exchange' && !selectedExchange)}
            >
              Add User
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

const UserManagementModal: React.FC<{
  user: UserWithPermissions
  initialTab: 'permissions' | 'password' | 'mfa' | 'settings'
  selectedExchange: string | 'account' | null
  exchanges: any[]
  currentExchange: any
  currentPermission?: ExchangeUserPermission
  accountUser?: AccountUser
  onClose: () => void
  onSave: (userId: string, exchangeId: string | 'account', updates: Partial<ExchangeUserPermission> & { allowedExchanges?: string[] }) => void
  onPasswordReset: (userId: string) => void
  onMFAReset: (userId: string) => void
}> = ({ user, initialTab, selectedExchange, exchanges, currentExchange, currentPermission, accountUser, onClose, onSave, onPasswordReset, onMFAReset }) => {
  const [activeTab, setActiveTab] = useState<'permissions' | 'password' | 'mfa' | 'settings'>(initialTab)
  const [passwordResetConfirmed, setPasswordResetConfirmed] = useState(false)
  const [mfaResetConfirmed, setMfaResetConfirmed] = useState(false)

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-3xl rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.12),0_4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_4px_12px_rgba(0,0,0,0.2)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200/50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                <Settings className="h-6 w-6 text-primary-600" />
                Manage User: {user.firstName} {user.lastName}
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                {user.email} • {selectedExchange === 'account' ? 'Account Level' : currentExchange?.name}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100/80 rounded-xl transition-colors">
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200/50 px-6">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('permissions')}
              className={`px-4 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'permissions'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Permissions
              </div>
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`px-4 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'password'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4" />
                Password
              </div>
            </button>
            <button
              onClick={() => setActiveTab('mfa')}
              className={`px-4 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'mfa'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                MFA
              </div>
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-4 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'settings'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Settings
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'permissions' && (
            <PermissionForm
              user={user}
              exchangeId={selectedExchange || 'account'}
              exchange={selectedExchange !== 'account' ? currentExchange : null}
              exchanges={exchanges}
              currentPermission={currentPermission}
              accountUser={accountUser}
              onSave={onSave}
              onClose={onClose}
            />
          )}

          {activeTab === 'password' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-1">Reset Password</h3>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      This will send a password reset email to <strong>{user.email}</strong>. The user will receive instructions to set a new password.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={passwordResetConfirmed}
                    onChange={(e) => setPasswordResetConfirmed(e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-700">I confirm I want to reset the password for this user</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200/50">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (passwordResetConfirmed) {
                      onPasswordReset(user.id)
                      alert('Password reset email sent successfully')
                      onClose()
                    }
                  }}
                  disabled={!passwordResetConfirmed}
                >
                  Send Reset Email
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'mfa' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-1">Reset MFA</h3>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      This will reset the multi-factor authentication for <strong>{user.firstName} {user.lastName}</strong>. They will need to set up MFA again on their next login.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mfaResetConfirmed}
                    onChange={(e) => setMfaResetConfirmed(e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-700">I confirm I want to reset MFA for this user</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200/50">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (mfaResetConfirmed) {
                      onMFAReset(user.id)
                      alert('MFA reset successfully. User will need to set up MFA again on next login.')
                      onClose()
                    }
                  }}
                  disabled={!mfaResetConfirmed}
                >
                  Reset MFA
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <RefreshCw className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-1">Session Management</h3>
                    <p className="text-sm text-blue-700">
                      Refresh the user's session token. This will require them to log in again on their next request.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200/50">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    alert('Session refreshed successfully')
                    onClose()
                  }}
                >
                  Refresh Session
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const PermissionForm: React.FC<{
  user: UserWithPermissions
  exchangeId: string | 'account'
  exchange: any
  exchanges: any[]
  currentPermission?: ExchangeUserPermission
  accountUser?: AccountUser
  onSave: (userId: string, exchangeId: string | 'account', updates: Partial<ExchangeUserPermission> & { allowedExchanges?: string[] }) => void
  onClose: () => void
}> = ({ user, exchangeId, exchange, exchanges, currentPermission, accountUser, onSave, onClose }) => {
  const [userType, setUserType] = useState<UserType>(currentPermission?.userType || 'member')
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>(currentPermission?.permissionLevel || 'read')
  const [featurePermissions, setFeaturePermissions] = useState<FeaturePermissions>(
    currentPermission?.featurePermissions || {}
  )
  const [customTypeName, setCustomTypeName] = useState(currentPermission?.customTypeName || '')
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>(
    accountUser?.allowedExchanges || []
  )

  const toggleFeature = (feature: keyof FeaturePermissions) => {
    setFeaturePermissions(prev => ({
      ...prev,
      [feature]: !prev[feature]
    }))
  }

  const toggleExchange = (exchangeId: string) => {
    setSelectedExchanges(prev => 
      prev.includes(exchangeId)
        ? prev.filter(id => id !== exchangeId)
        : [...prev, exchangeId]
    )
  }

  const selectAllExchanges = () => {
    setSelectedExchanges(exchanges.map(e => e.id))
  }

  const deselectAllExchanges = () => {
    setSelectedExchanges([])
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-3">
          User Type
        </label>
        <div className="grid grid-cols-2 gap-3">
          {(['owner', 'accounting', 'real_estate_agent', 'attorney', 'member', 'viewer', 'custom'] as UserType[]).map((type) => {
            const Icon = USER_TYPE_ICONS[type]
            const isSelected = userType === type
            return (
              <button
                key={type}
                onClick={() => setUserType(type)}
                className={`p-4 rounded-2xl border-2 text-left transition-all ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50/80 backdrop-blur-sm shadow-lg shadow-primary-500/10'
                    : 'border-slate-200 hover:border-slate-300 bg-white/60 backdrop-blur-sm'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Icon className={`h-5 w-5 ${isSelected ? 'text-primary-600' : 'text-slate-400'}`} />
                  <div className={`font-medium text-sm ${isSelected ? 'text-primary-700' : 'text-slate-700'}`}>{USER_TYPE_LABELS[type]}</div>
                </div>
                <div className="text-xs text-slate-600">{USER_TYPE_DESCRIPTIONS[type]}</div>
              </button>
            )
          })}
        </div>
        {/* Show access level description */}
        <div className="mt-3 p-3 rounded-xl bg-slate-50/80 backdrop-blur-sm border border-slate-200/50">
          <div className="flex items-start gap-2">
            <Shield className="h-4 w-4 text-primary-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-xs font-medium text-slate-700 mb-1">Access Level:</div>
              <div className="text-xs text-slate-600">{USER_TYPE_DESCRIPTIONS[userType]}</div>
            </div>
          </div>
        </div>
        {/* Custom type name input when custom is selected */}
        {userType === 'custom' && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Custom Type Name
            </label>
            <Input
              placeholder="e.g., Project Manager, Consultant, etc."
              value={customTypeName || ''}
              onChange={(e) => setCustomTypeName(e.target.value)}
              className="bg-white/60 backdrop-blur-sm border-slate-200/50"
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-3">
          Permission Level
        </label>
        <div className="grid grid-cols-3 gap-3">
          {(['read', 'write', 'read_only'] as PermissionLevel[]).map((level) => {
            const Icon = PERMISSION_ICONS[level]
            const isSelected = permissionLevel === level
            return (
              <button
                key={level}
                onClick={() => setPermissionLevel(level)}
                className={`p-4 rounded-2xl border-2 transition-all ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50/80 backdrop-blur-sm shadow-lg shadow-primary-500/10'
                    : 'border-slate-200 hover:border-slate-300 bg-white/60 backdrop-blur-sm'
                }`}
              >
                <Icon className={`h-5 w-5 mx-auto mb-2 ${isSelected ? 'text-primary-600' : 'text-slate-400'}`} />
                <div className={`font-medium text-sm text-center ${isSelected ? 'text-primary-700' : 'text-slate-700'}`}>{PERMISSION_LABELS[level]}</div>
              </button>
            )
          })}
        </div>
      </div>

      {exchangeId === 'account' && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Select Exchanges
          </label>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-slate-600">
              {selectedExchanges.length === 0 
                ? 'No exchanges selected (user will have access to all exchanges)'
                : `${selectedExchanges.length} of ${exchanges.length} exchanges selected`
              }
            </span>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={selectAllExchanges}
                className="text-xs"
              >
                Select All
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={deselectAllExchanges}
                className="text-xs"
              >
                Clear All
              </Button>
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto border border-slate-200/50 rounded-xl bg-white/60 backdrop-blur-sm p-3 space-y-2">
            {exchanges.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4">No exchanges available</div>
            ) : (
              exchanges.map((ex) => {
                const isSelected = selectedExchanges.includes(ex.id)
                return (
                  <button
                    key={ex.id}
                    onClick={() => toggleExchange(ex.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50/80 backdrop-blur-sm'
                        : 'border-slate-200 hover:border-slate-300 bg-white/60 backdrop-blur-sm'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className={`h-4 w-4 ${isSelected ? 'text-primary-600' : 'text-slate-400'}`} />
                      <div className="text-left">
                        <div className={`text-sm font-medium ${isSelected ? 'text-primary-700' : 'text-slate-700'}`}>
                          {ex.name}
                        </div>
                        {ex.address && (
                          <div className="text-xs text-slate-500 mt-0.5">{ex.address}</div>
                        )}
                      </div>
                    </div>
                    {isSelected ? (
                      <CheckCircle className="h-5 w-5 text-primary-600" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-slate-300" />
                    )}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}

      {exchangeId !== 'account' && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Feature Permissions
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(FEATURE_LABELS)
              .filter(([feature]) => {
                const typeFeatures = USER_TYPE_FEATURES[userType]
                return typeFeatures === 'custom' || typeFeatures.includes(feature as keyof typeof FEATURE_LABELS)
              })
              .map(([feature, label]) => {
                const Icon = FEATURE_ICONS[feature as keyof typeof FEATURE_ICONS]
                const enabled = featurePermissions[feature as keyof FeaturePermissions] || false
                return (
                  <button
                    key={feature}
                    onClick={() => toggleFeature(feature as keyof FeaturePermissions)}
                    className={`p-4 rounded-2xl border-2 transition-all text-left ${
                      enabled
                        ? 'border-primary-500 bg-primary-50/80 backdrop-blur-sm shadow-lg shadow-primary-500/10'
                        : 'border-slate-200 hover:border-slate-300 bg-white/60 backdrop-blur-sm'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      {Icon && <Icon className={`h-5 w-5 ${enabled ? 'text-primary-600' : 'text-slate-400'}`} />}
                      {enabled ? (
                        <CheckCircle className="h-5 w-5 text-primary-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                    <div className={`font-medium text-sm ${enabled ? 'text-primary-700' : 'text-slate-700'}`}>{label}</div>
                  </button>
                )
              })}
          </div>
        </div>
      )}

      <div className="flex space-x-3 pt-4 border-t border-slate-200/50">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Cancel
        </Button>
        <Button 
          onClick={() => {
            onSave(user.id, exchangeId, {
              userType,
              permissionLevel,
              featurePermissions: exchangeId !== 'account' ? featurePermissions : undefined,
              customTypeName: userType === 'custom' ? customTypeName : undefined,
              allowedExchanges: exchangeId === 'account' ? selectedExchanges : undefined
            })
            onClose()
          }} 
          className="flex-1"
        >
          Save Changes
        </Button>
      </div>
    </div>
  )
}

const EditPermissionModal: React.FC<{
  user: UserWithPermissions
  exchangeId: string | 'account'
  exchange: any
  exchanges: any[]
  currentPermission?: ExchangeUserPermission
  accountUser?: AccountUser
  onClose: () => void
  onSave: (userId: string, exchangeId: string | 'account', updates: Partial<ExchangeUserPermission> & { allowedExchanges?: string[] }) => void
}> = ({ user, exchangeId, exchange, exchanges, currentPermission, accountUser, onClose, onSave }) => {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-3xl rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.12),0_4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_4px_12px_rgba(0,0,0,0.2)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200/50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                <Settings className="h-6 w-6 text-primary-600" />
                Manage Permissions
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                {user.firstName} {user.lastName} • {exchangeId === 'account' ? 'Account Level' : exchange?.name}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100/80 rounded-xl transition-colors">
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <PermissionForm
            user={user}
            exchangeId={exchangeId}
            exchange={exchange}
            exchanges={exchanges}
            currentPermission={currentPermission}
            accountUser={accountUser}
            onSave={onSave}
            onClose={onClose}
          />
        </div>
      </div>
    </div>
  )
}

